<template>
  <div class="chart-container">
    <div class="chart-header">
      <Dropdown 
        :titleDropdown="selectedChart.name"
        position="right"
        colorArrow="black"
      >
        <ul>
          <li 
            v-for="chart in availableCharts" 
            :key="chart.type"
            @click="changeChartType(chart.type)"
            class="bkps-dropdown-item"
            :title="chart.description"
          >
            <i :class="chart.icon"></i>
            {{ chart.name }}
          </li>
        </ul>
      </Dropdown>
      
      <Dropdown 
        :titleDropdown="selectedSort.name"
        position="right"
        colorArrow="black"
        class="ml-2"
      >
        <ul>
          <li 
            v-for="sort in availableSorts" 
            :key="sort.type"
            @click="changeSortType(sort.type)"
            class="bkps-dropdown-item"
            :title="sort.description"
          >
            <i :class="sort.icon"></i>
            {{ sort.name }}
          </li>
        </ul>
      </Dropdown>
    </div>
    <div ref="targetChart" style="height: 350px; width: 100%; margin-top: 10px;"></div>
  </div>
</template>

<script>
import Highcharts from 'highcharts/es5/highcharts'
import accessibility from 'highcharts/es5/modules/accessibility'

import ChartMix from '@/components/charts/ChartMix.vue'
import ChartThemeMixin from '@/components/charts/ChartThemeMixin.vue'
import Dropdown from '@/components/shares/specialTag/Dropdown.vue'
import { groupMultasByCategory } from '@/utils/multas-grouping'

accessibility(Highcharts)

const calculatePercentage = (value, total) => { return (value * 100) / total }
const withoutClassification = 'SEM CLASSIFICAÇÃO'
const MAX_LABEL_CHARS = 30
const truncateLabel = (text) => {
  if (!text) return ''
  return text.length > MAX_LABEL_CHARS ? `${text.slice(0, MAX_LABEL_CHARS).trimEnd()}...` : text
}

export default {
  components: { Dropdown },
  props: {
    data: { required: true, type: Array },
    graphTitle: { required: true, type: String },
    loading: { required: true, type: Boolean },
  },
  data() {
    return {
      chartType: 'pie',
      sortBy: 'value',
      useGrouping: true,
      availableCharts: [
        {
          type: 'pie',
          name: 'Pizza',
          icon: 'fas fa-chart-pie',
          description: 'Gráfico de pizza'
        },
        {
          type: 'donut',
          name: 'Rosca',
          icon: 'fas fa-dot-circle',
          description: 'Gráfico donut'
        },
        {
          type: 'column',
          name: 'Barras',
          icon: 'fas fa-chart-bar',
          description: 'Gráfico de barras'
        },
        {
          type: 'bar',
          name: 'Horizontais',
          icon: 'fas fa-chart-bar',
          description: 'Gráfico de barras horizontais'
        }
      ],
      availableSorts: [
        {
          type: 'value',
          name: 'Valor',
          icon: 'fas fa-dollar-sign',
          description: 'Ordenar por valor'
        },
        {
          type: 'count',
          name: 'Qtd',
          icon: 'fas fa-hashtag',
          description: 'Ordenar por quantidade'
        }
      ]
    }
  },
  computed: {
    selectedChart() {
      return this.availableCharts.find(chart => chart.type === this.chartType)
    },
    selectedSort() {
      return this.availableSorts.find(sort => sort.type === this.sortBy)
    }
  },
  methods: {
    changeChartType(type) {
      this.chartType = type
      this.createChart()
    },
    changeSortType(type) {
      this.sortBy = type
      this.createChart()
    },
    buildSeries() {
      const serie = []

      const dataToProcess = this.useGrouping ? groupMultasByCategory(this.data) : this.data

      const total = dataToProcess.map(t => t.sum).reduce((n, n1) => n + n1, 0)
      const totalCount = dataToProcess.map(t => t.count).reduce((n, n1) => n + n1, 0)

      dataToProcess.forEach(item => {
        const serieName = item.descricao || withoutClassification
        const valueInReais = item.sum / 100
        const percentage = this.sortBy === 'value'
          ? calculatePercentage(item.sum, total)
          : calculatePercentage(item.count, totalCount)

        serie.push({
          name: serieName,
          displayName: truncateLabel(serieName),
          y: percentage,
          rawValue: valueInReais,
          count: item.count
        })
      })

      const serieFiltered = serie.filter(item => item.y > 0)
      serieFiltered.sort((a, b) => b.y - a.y)

      const maxItems = 8
      let topSerie = serieFiltered.slice(0, maxItems)

      if (serieFiltered.length > maxItems) {
        const others = serieFiltered.slice(maxItems)
        const othersSum = {
          name: 'Outros',
          displayName: 'Outros',
          y: others.reduce((sum, item) => sum + item.y, 0),
          rawValue: others.reduce((sum, item) => sum + item.rawValue, 0),
          count: others.reduce((sum, item) => sum + item.count, 0),
          color: '#CCCCCC'
        }
        topSerie.push(othersSum)
      }

      return { serie: topSerie }
    },
    createChart() {
      const theme = this.chartTheme
      const { serie } = this.buildSeries()
      const isDonut = this.chartType === 'donut'
      const isPie = this.chartType === 'pie' || this.chartType === 'donut'
      const isBar = ['column', 'bar'].includes(this.chartType)

      const colorConnectors = function (chart) {
        const series = chart.series && chart.series[0]
        if (!series) return
        series.points.forEach(function (point) {
          if (point.connector) {
            point.connector.attr({ stroke: point.color })
          }
        })
      }

      const repositionSmallTopLabels = function (chart) {
        const series = chart.series && chart.series[0]
        if (!series || !series.center) return

        const center = series.center
        const centerX = chart.plotLeft + center[0]
        const centerY = chart.plotTop + center[1]
        const radius = center[2] / 2
        const plotLeft = chart.plotLeft
        const plotRight = chart.plotLeft + chart.plotWidth
        const plotTop = chart.plotTop

        const candidates = []
        series.points.forEach(function (point) {
          if (!point.dataLabel || typeof point.y !== 'number' || !point.shapeArgs) return
          if (point.y >= 3) return
          const slicePoint = point.shapeArgs
          const midAngle = (slicePoint.start + slicePoint.end) / 2
          const sliceDirY = Math.sin(midAngle - Math.PI / 2)
          if (sliceDirY >= 0) return
          const sliceDirX = Math.cos(midAngle - Math.PI / 2)
          const bbox = point.dataLabel.getBBox()
          candidates.push({ point, bbox, sliceDirX, sliceDirY, midAngle })
        })

        if (candidates.length === 0) return

        candidates.sort((a, b) => a.sliceDirX - b.sliceDirX)

        const placed = []
        candidates.forEach((item) => {
          const sliceOuterX = centerX + item.sliceDirX * radius
          const minCenterX = plotLeft + item.bbox.width / 2 + 4
          const maxCenterX = plotRight - item.bbox.width / 2 - 4
          const labelCenterX = Math.max(minCenterX, Math.min(sliceOuterX, maxCenterX))
          const labelX = labelCenterX - item.bbox.width / 2

          let row = 0
          while (placed.some(p => p.row === row
              && !(labelX + item.bbox.width + 4 < p.x || labelX > p.x + p.w + 4))) {
            row++
          }
          const labelTop = plotTop + 4 + row * (item.bbox.height + 4)

          placed.push({ x: labelX, w: item.bbox.width, row })

          item.point.dataLabel.attr({ x: labelX, y: labelTop + item.bbox.height - 4 })

          if (item.point.connector) {
            const startX = centerX + item.sliceDirX * radius
            const startY = centerY + item.sliceDirY * radius
            const endX = labelCenterX
            const endY = labelTop + item.bbox.height + 1
            item.point.connector.attr({
              d: ['M', startX, startY, 'L', endX, endY].join(' '),
              stroke: item.point.color
            })
          }
        })
      }

      const pieLabelFormat = '{point.displayName}: {point.percentage:.2f}%'

      const chartConfig = {
        chart: {
          backgroundColor: theme.backgroundColor,
          type: isPie ? 'pie' : this.chartType,
          height: isPie ? 350 : 500,
          margin: isPie ? [60, 100, 60, 100] : undefined,
          style: { fontFamily: '"Montserrat", sans-serif' }
        },
        colors: theme.colors,
        credits: {
          enabled: false
        },
        title: {
          text: this.currentTitle,
          align: 'center',
          style: {
            color: theme.textColor,
            fontSize: '18px',
            fontWeight: '600'
          }
        },
        accessibility: {
          announceNewData: {
            enabled: true
          },
        },
        plotOptions: {
          series: {
            borderRadius: 5
          },
          pie: {
            size: '75%',
            innerSize: '0%'
          }
        },
        tooltip: {
          backgroundColor: theme.backgroundColor,
          borderColor: theme.gridLineColor,
          style: { color: theme.textColor },
          headerFormat: '',
          pointFormat: `<span style="color:{point.color}">{point.name}</span><br/>
                       Porcentagem: <b>{point.y:.2f}%</b><br/>
                       Valor: R$ {point.rawValue:,.2f}<br/>
                       Quantidade: {point.count}`
        },
        series: [
          {
            name: 'Situações',
            colorByPoint: true,
            data: serie
          }
        ]
      }

      if (isDonut) {
        chartConfig.chart.events = {
          render: function () {
            colorConnectors(this)
            repositionSmallTopLabels(this)
          }
        }
        chartConfig.plotOptions.pie = {
          allowPointSelect: false,
          cursor: 'default',
          borderRadius: 8,
          borderWidth: 3,
          borderColor: theme.backgroundColor,
          dataLabels: {
            enabled: true,
            distance: 25,
            connectorWidth: 2,
            connectorShape: 'straight',
            softConnector: false,
            format: pieLabelFormat,
            style: {
              fontWeight: '600',
              color: '#000000',
              textOutline: 'none'
            }
          },
          innerSize: '60%',
          size: '80%'
        }
        chartConfig.legend = { enabled: false }
      } else if (isPie) {
        chartConfig.chart.events = {
          render: function () {
            colorConnectors(this)
            repositionSmallTopLabels(this)
          }
        }
        chartConfig.plotOptions.pie = {
          allowPointSelect: false,
          cursor: 'default',
          dataLabels: {
            enabled: true,
            distance: 25,
            connectorWidth: 2,
            connectorShape: 'straight',
            softConnector: false,
            format: pieLabelFormat,
            style: {
              color: '#000000',
              textOutline: 'none',
              fontSize: '11px',
              fontWeight: '600'
            }
          },
          size: '80%'
        }
        chartConfig.legend = { enabled: false }
      }

      if (isBar) {
        const categories = serie.map(item => truncateLabel(item.name))
        const data = serie.map(item => ({
          name: item.name,
          y: item.y,
          rawValue: item.rawValue,
          count: item.count
        }))

        chartConfig.chart.marginTop = 40
        chartConfig.chart.marginBottom = this.chartType === 'column' ? 100 : 60
        chartConfig.chart.marginLeft = this.chartType === 'bar' ? 200 : 70
        chartConfig.chart.marginRight = 20
        chartConfig.chart.height = 350
        chartConfig.xAxis = {
          categories: categories,
          labels: {
            rotation: this.chartType === 'column' ? -45 : 0,
            style: {
              fontSize: '10px',
              color: theme.textColor
            }
          }
        }
        chartConfig.yAxis = {
          title: {
            text: this.sortBy === 'value' ? 'Percentual por Valor' : 'Percentual por Quantidade',
            style: { color: theme.textColor }
          },
          labels: { style: { color: theme.textColor } },
          gridLineColor: theme.gridLineColor
        }
        const barDataLabels = {
          enabled: true,
          format: '{point.y:.2f}%',
          style: {
            color: theme.textColor,
            textOutline: 'none',
            fontSize: '11px'
          }
        }
        chartConfig.plotOptions.column = {
          borderRadius: 6,
          borderWidth: 0,
          dataLabels: barDataLabels
        }
        chartConfig.plotOptions.bar = {
          borderRadius: 6,
          borderWidth: 0,
          dataLabels: barDataLabels
        }
        chartConfig.legend = {
          enabled: false
        }
        chartConfig.series[0] = {
          type: this.chartType,
          name: 'Situações',
          colorByPoint: true,
          data: data
        }
      }

      Highcharts.chart(this.$refs.targetChart, chartConfig)
    }
  },
  mixins: [ChartMix, ChartThemeMixin],
  mounted() {
    this.createChart()
  },
  watch: {
    isDarkTheme: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      immediate: false
    },
    data: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      deep: true
    }
  },
  name: 'CmEmbarcadorDescricaoChart'
}
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: visible;
  background-color: var(--white-bg);
  padding: 15px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
}

.chart-header {
  position: absolute;
  top: 15px;
  right: 20px;
  z-index: 10;
  display: flex;
  gap: 8px;
}

/* CSS específico para o dropdown dos gráficos */
.chart-header .bkps-dropdown {
  background: var(--white-bg) !important;
  border: 1px solid var(--border-color) !important;
  color: var(--primary-text-color) !important;
}

.chart-header .bkps-dropdown-content {
  background-color: var(--white-bg) !important;
  color: var(--primary-text-color) !important;
  border: 1px solid var(--border-color) !important;
}

.chart-header .bkps-dropdown-item {
  color: var(--primary-text-color) !important;
}

.chart-header .bkps-dropdown-item:hover {
  background-color: var(--btn-hover-bg) !important;
}

.chart-header .hide {
  max-height: 0 !important;
  padding: 0 !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
}
</style>

<style>
/* Melhorar área clicável da legenda do Highcharts */
.highcharts-legend-item {
  cursor: pointer !important;
  padding: 6px 0 !important;
}

.highcharts-legend-item text {
  cursor: pointer !important;
  pointer-events: all !important;
}

.highcharts-legend-item rect {
  cursor: pointer !important;
}

.highcharts-legend-item:hover {
  opacity: 0.8;
}

.highcharts-legend-item:hover text {
  font-weight: bold !important;
}

.highcharts-legend-item:hover .highcharts-point {
  fill-opacity: 0.8 !important;
}

/* Aumentar área clicável adicionando padding invisível */
.highcharts-legend-item > * {
  pointer-events: all !important;
}
</style>
